import React, { useState, useEffect } from 'react';
import { UsersDropdown } from '../../../components/layout/form-fields/inputs/UsersDropdown';
import UserProfileAvatar from '../user-profile-avatar/UserProfileAvatar';

const UserAssignmentInput = ({
	value = null,
	isMulti = false,
	placeholder = 'Select user',
	role = null,
	minSearchChars = 3,
	disabled = false,
	onAssignToMe,
	onChange,
	className = '',
	showAvatar = true,
	customStyles = {},
	customFieldActions = null
}) => {
	const [selectedUser, setSelectedUser] = useState(null);
	const [assignedUsers, setAssignedUsers] = useState([]);
	const [primaryUserId, setPrimaryUserId] = useState(null);

	useEffect(() => {
		if (value) {
			if (isMulti && Array.isArray(value)) {
				setAssignedUsers(value);

				const existingPrimaryUser = value.find(user => user.isPrimary === true);

				if (existingPrimaryUser) {
					// Use the existing primary user
					setPrimaryUserId(existingPrimaryUser.id);
				} else if (value.length > 0 && !primaryUserId) {
					// Set the first user as primary by default if no primary is set
					setPrimaryUserId(value[0].id);
				}
			} else if (!isMulti && value) {
				setSelectedUser(value);
			}
		}
	}, [value, isMulti, primaryUserId]);

	const handleSave = value => {
		setSelectedUser(value);

		if (isMulti) {
			if (value) {
				const newUser = {
					...value,
					isPrimary: assignedUsers.length === 0
				};
				const updatedUsers = [...assignedUsers, newUser];
				setAssignedUsers(updatedUsers);

				// Set as primary if it's the first user
				if (assignedUsers.length === 0) {
					setPrimaryUserId(value.id);
				}

				if (onChange) {
					onChange(updatedUsers);
				}
			}

			setSelectedUser(null);
		} else {
			if (onChange) {
				onChange(value);
			}
		}
	};

	const handleAssignToMe = e => {
		e.preventDefault();
		if (onAssignToMe) {
			onAssignToMe();
		}
	};

	const handleSetPrimary = userId => {
		setPrimaryUserId(userId);
		const updatedUsers = assignedUsers.map(user => ({
			...user,
			isPrimary: user.id === userId
		}));
		setAssignedUsers(updatedUsers);
		if (onChange) {
			onChange(updatedUsers);
		}
	};

	const handleRemoveUser = userId => {
		const updatedUsers = assignedUsers.filter(user => user.id !== userId);
		setAssignedUsers(updatedUsers);

		// If removing the primary user, set the first remaining user as primary
		if (primaryUserId === userId && updatedUsers.length > 0) {
			setPrimaryUserId(updatedUsers[0].id);
			updatedUsers[0].isPrimary = true;
		} else if (updatedUsers.length === 0) {
			setPrimaryUserId(null);
		}

		if (onChange) {
			onChange(updatedUsers);
		}
	};

	const renderDisplayMode = () => {
		const assignedUserIds = assignedUsers.map(user => user.id);

		return (
			<div
				className="user-assignment-edit"
				style={{
					...(customStyles?.control || {})
				}}>
				<UsersDropdown
					value={selectedUser}
					placeholder={placeholder}
					styleVersion="ats"
					role={role}
					minSearchChars={minSearchChars}
					showAvatar={showAvatar}
					excludeUserIds={assignedUserIds}
					onValueChange={value => {
						handleSave(value);
					}}
					isClearable
				/>

				<div className="field-actions">
					{!disabled && (
						<>
							{onAssignToMe && (
								<button
									className="assign-to-me-btn"
									onClick={handleAssignToMe}
									type="button">
									Assign to me
								</button>
							)}
							{customFieldActions}
						</>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className={`user-assignment-input editing ${className}`}>
			{renderDisplayMode()}
			{assignedUsers.length > 0 && (
				<div className="assigned-users">
					{assignedUsers.map(user => (
						<div key={user.id} className="assigned-user-row">
							<div className="user-info">
								<button
									className="set-primary-btn"
									title="Primary"
									type="button"
									onClick={e => {
										e.preventDefault();
										handleSetPrimary(
											user.id === primaryUserId ? null : user.id
										);
									}}>
									<img
										src={
											user.id === primaryUserId
												? '/ats/icons/star-filled.svg'
												: '/ats/icons/star-empty.svg'
										}
										alt="Star"
									/>
								</button>
								<UserProfileAvatar
									imageUrl={user.imageUrl}
									name={user.name}
									showName={true}
									size={21}
								/>
							</div>
							<div className="user-actions">
								<button
									title="Remove User"
									type="button"
									onClick={() => handleRemoveUser(user.id)}>
									<img src="/ats/icons/trash-icon.svg" alt="Delete" />
								</button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default UserAssignmentInput;
